import React, { useState, useEffect, Fragment } from 'react';
import { forwardRef } from 'react';
import Grid from '@material-ui/core/Grid';
import MaterialTable from '@material-table/core';
import AddBox from '@material-ui/icons/AddBox';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import axios from 'axios';
import Alert from '@material-ui/lab/Alert';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { createTheme } from '@material-ui/core/styles';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ExportCsv, ExportPdf } from '@material-table/exporters';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

export default function ReservationsReportFull() {
  // Use this url for testing
  // username: uniper+report+user password: E7yzc3q8zEw9nDym
  // http://localhost:3000/#/reservations_report_full?organization=153&location=199&t=4677042a-549f-487a-bba4-9fe0e6b50913

  let date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  const epochTime = date.getTime();
  const [startDate, setStartDate] = useState(epochTime - 604800000); // search results 1 week before start time
  const [endDate, setEndDate] = useState(epochTime);

  if (typeof startDate !== 'number') {
    setStartDate(startDate.getTime());
  }
  if (typeof endDate !== 'number') {
    setEndDate(endDate.getTime());
  }
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orgId = queryParams.get('organization');
  const locId = queryParams.get('location');
  const { token } = useAuth();
  const [data, setData] = useState([]);
  const [iserror] = useState(false);
  const [errorMessages] = useState([]);
  const measurementsUrl = `https://eu-api.empathicbuilding.com/v1/organizations/${orgId}/locations/${locId}/measurements/reservation?start=${
    startDate - 604800000
  }&end=${endDate}&ignore_previous_validity=true&limit=5000`;

  const assetsUrl = `https://eu-api.empathicbuilding.com/v1/organizations/${orgId}/locations/${locId}/assets`;

  const api = axios.create({
    baseURL: 'https://eu-api.empathicbuilding.com/v1',
    headers: { Authorization: `Bearer ${token}` },
  });
  var columns = [
    {
      title: 'reservation_date',
      field: 'reservation_date',
    },
    {
      title: 'reservations_count',
      field: 'reservations_count',
    },
    {
      title: 'assets_count',
      field: 'asset_count',
    },
    {
      title: 'asset(s)',
      field: 'asset_id',
      export: false,
      hidden: true,
      render: (rowData) => (
        <div>
          {rowData.asset_id.map((asset) => (
            <>
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={`https://empathicbuilding.com/eb/${orgId}/${locId}/asset/${asset.id}`}
              >
                {asset.name}{' '}
              </a>
              <Fragment>&nbsp;</Fragment>
            </>
          ))}
        </div>
      ),
    },
    {
      title: 'assets',
      field: 'assets',
      width: null,
    },
  ];

  const groupBy = (array, key) => {
    return array.reduce((result, currentItem) => {
      (result[currentItem[key]] = result[currentItem[key]] || []).push(
        currentItem
      );
      return result;
    }, {});
  };

  const getAsset = (sensor_id, assets) => {
    return assets.data.find((asset) => {
      return asset.data_providers?.find(
        (data_provider) => data_provider === sensor_id
      );
    });
  };

  useEffect(() => {
    let assets = [];
    let final_list = [];
    let grouped_list = [];
    const fetchData = async () => {
      const measurements = await api.get(measurementsUrl);
      assets = await api.get(assetsUrl);
      let sensor_ids = [];
      let asset_ids = [];
      let asset = 0;
      let assetnames = '';
      let reservations_list = [];

      measurements.data.rows.forEach((reservation) => {
        if (
          typeof reservation[3] !== 'undefined' &&
          reservation[3].length > 0
        ) {
          reservation[3].forEach((item) => {
            if (item.start >= startDate && item.end <= endDate) {
              reservations_list.push({
                reservation_date: new Date(item['start']).toDateString(),
                sensor_id: reservation[2],
                start: new Date(item['start']).toLocaleString(),
                end: new Date(item['end']).toLocaleString(),
                subject: item['subject'],
              });
            }
          });
        }
      });
      reservations_list = reservations_list.sort(function compare(obj1, obj2) {
        return (
          new Date(obj1.reservation_date) - new Date(obj2.reservation_date)
        );
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
      grouped_list = groupBy(reservations_list, 'reservation_date');

      Object.entries(grouped_list).forEach((item) => {
        if (item[1].length > 0) {
          Object.entries(item[1]).forEach((sensor) => {
            if (!sensor_ids.includes(sensor[1]['sensor_id'])) {
              sensor_ids.push(sensor[1]['sensor_id']);
              asset = getAsset(sensor[1]['sensor_id'], assets);
              if (asset) {
                if (
                  !asset_ids.some((arrayasset) => arrayasset.id === asset.id)
                ) {
                  asset_ids.push({ id: asset.id, name: asset.name });
                  assetnames = assetnames.concat(asset.name, ' ');
                }
              }
            }
          });
        }
        final_list.push({
          reservation_date: new Date(item[0]).toDateString(),
          reservations_count: item[1].length,
          asset_id: asset_ids,
          asset_count: asset_ids.length,
          assets: assetnames,
        });
        sensor_ids = [];
        asset_ids = [];
        asset = 0;
        assetnames = '';
      });
      setData(final_list);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const theme = createTheme({
    palette: {
      primary: {
        main: '#4caf50',
      },
      secondary: {
        main: '#ff9100',
      },
    },
  });

  return (
    <MuiThemeProvider theme={theme}>
      <div>
        <Grid container spacing={1} style={{ width: '100%' }}>
          <Grid item xs={3}></Grid>
          <Grid item xs={6}>
            <div>
              {iserror && (
                <Alert severity="error">
                  {errorMessages.map((msg, i) => {
                    return <div key={i}>{msg}</div>;
                  })}
                </Alert>
              )}
            </div>
            <h1>Reservations report - Location</h1>

            <div
              style={{
                display: 'flex',
                gap: '2rem',
                justifyContent: 'flex-start',
                marginBottom: '1rem',
              }}
            >
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                  label="Start date"
                  format="dd/MM/yyyy"
                  value={startDate}
                  onChange={setStartDate}
                  style={{ width: '7rem' }}
                />
                <DatePicker
                  label="End date"
                  format="dd/MM/yyyy"
                  value={endDate}
                  onChange={setEndDate}
                  style={{ width: '7rem' }}
                />
              </MuiPickersUtilsProvider>
            </div>

            <MaterialTable
              title="Reservations report - Location"
              columns={columns}
              data={data}
              icons={tableIcons}
              options={{
                exportMenu: [
                  {
                    label: 'Export CSV',
                    exportFunc: (cols, datas) =>
                      ExportCsv(cols, datas, 'Reservations_report'),
                  },
                  {
                    label: 'Export PDF',
                    //// You can do whatever you wish in this function. We provide the
                    //// raw table columns and table data for you to modify, if needed.
                    // exportFunc: (cols, datas) => console.log({ cols, datas })
                    exportFunc: (cols, datas) =>
                      ExportPdf(cols, datas, 'Reservations_report'),
                  },
                ],
              }}
            />
          </Grid>
          <Grid item xs={3}></Grid>
        </Grid>
      </div>
    </MuiThemeProvider>
  );
}
